/**
 * Conversational MENU PATH RESOLUTION — the missing half of
 * `menuPricingResolver.ts` for assistants that have no navigation context.
 *
 * `resolveItemPrice` is driven by `menuGroupPath`, the ROOT→LEAF chain of group
 * ids the customer NAVIGATED to reach an item. A guest on the phone or in chat
 * navigates NOTHING — they say "a large pepperoni pizza". There is no path.
 *
 * Reading `item.basePrice` to paper over that is a charging bug: the server
 * trusts the cart-stored price and does not recompute group pricing (see the
 * header of `menuPricingResolver.ts`). So instead we ENUMERATE the paths the
 * item is legitimately reachable by right now, price each one, and hand the
 * caller either a single unambiguous answer or an explicit ambiguity it must
 * resolve by ASKING the guest.
 *
 * The menu is a DAG, not a tree — `IMenuGroup.parentIds` is an array and a
 * subgroup may hang off several parents — which is exactly why one item can
 * carry more than one legitimate price (lunch vs. dinner, dine-in vs. online).
 * Children come from `parentIds` (the till's rule), walked by `menuTree.ts`'s
 * `walkMenuPaths` — never from `subgroups`, which holds only a parent's
 * per-child override entry and is frequently empty or stale (see menuTree.ts).
 *
 * Availability is per-EDGE, not just per-group: `ISubgroupReference` carries its
 * own `availability` / `daysAvailable` / `dateRange` / `seasonalAvailability` /
 * `visibleInOnlineOrdering` overrides, mirroring the per-parent pricing
 * overrides. A path is orderable only if every group AND every edge along it
 * allows it now.
 *
 * Pure functions, no Firestore, no React. See docs/ORDERUP_AI_ARCHITECTURE.md
 * (invariant B) and docs/MENU_PRICING_ARCHITECTURE.md.
 */

import type { IMenuGroup } from './MenuGroup';
import type { IItem } from './Item';
import {
  isItemVisibleOnline,
  isSubgroupEdgeAvailableOnline,
} from './channelVisibility';
import { isMenuGroupAvailableForOnlineOrdering } from './menuGroupAvailability';
import { resolveItemPrice, type ResolvedItemPrice } from './menuPricingResolver';
import { walkMenuPaths, type MenuEdge } from './menuTree';
import type { OnlineStorefrontScope } from './websiteMenuSelection';

/** One legitimate way a guest could have reached this item, already priced. */
export interface OrderablePath {
  /** ROOT→LEAF chain to feed straight into `resolveItemPrice` / the cart. */
  readonly menuGroupPath: number[];
  /** Leaf group id — the group the item actually hangs in. */
  readonly menuGroupId: number;
  /** Human-readable ROOT→LEAF names, for asking the guest to choose. */
  readonly pathNames: string[];
  /** The price this path resolves to. */
  readonly price: ResolvedItemPrice;
}

export interface PathResolution {
  /** Orderable paths, cheapest-flat-first then by shortest path for stability. */
  readonly paths: OrderablePath[];
  /**
   * TRUE when the surviving paths do not agree on price. The caller MUST ask
   * the guest which menu they mean — picking one silently mis-charges.
   */
  readonly isAmbiguous: boolean;
  /** Distinct price signatures across `paths`. 0 when unreachable. */
  readonly distinctPriceCount: number;
}

/**
 * A stable signature for a resolved price, used to decide whether two paths
 * actually differ. Two paths that resolve to the same price are NOT ambiguous
 * even though they are different paths — asking the guest would be noise.
 */
export function priceSignature(price: ResolvedItemPrice): string {
  switch (price.kind) {
    case 'flat':
      return `flat:${price.unitPrice}`;
    case 'open':
      return 'open';
    case 'sizes':
      return `sizes:${price.sizes.map((s) => `${s.name}=${s.price}`).join('|')}`;
  }
}

/**
 * Enumerate every currently-orderable ROOT→LEAF path to `item`, each priced
 * through the canonical resolver.
 *
 * @param item          the item the guest asked for
 * @param allMenuGroups every group for the restaurant
 * @param now           evaluation instant (injected for deterministic tests)
 * @param timeZone      the restaurant's IANA zone — schedules are its wall clock
 * @param storefront    the storefront the guest is ordering on
 *                      (`resolveOnlineStorefrontScope`): paths start at the menu
 *                      it opens on — or the menus its picker offers — and enter
 *                      no section the website hides, so a guest is never offered
 *                      an item checkout refuses (the server holds every cart line
 *                      to the same scope). Absent: every root, every section.
 *
 * Scheduled menu pricing does NOT enter here: with the tenant's flag on, the
 * guest assistant asks `resolveOnlineScheduledPaths` — the rule the storefront
 * and the server's cart re-pricing use — for the ONE route, so the assistant
 * writes exactly the route the server accepts.
 */
export function findOrderablePathsForItem(
  item: IItem,
  allMenuGroups: IMenuGroup[],
  now: Date = new Date(),
  timeZone?: string | null,
  storefront?: OnlineStorefrontScope,
): PathResolution {
  const itemId = item.itemId;
  if (itemId == null) {
    return { paths: [], isAmbiguous: false, distinctPriceCount: 0 };
  }
  // An item switched off for online ordering has no orderable path, whichever
  // group lists it — the management Visibility tab's "Online Ordering" toggle.
  // Every caller of this resolver is a guest-facing online surface.
  if (!isItemVisibleOnline(item)) {
    return { paths: [], isAmbiguous: false, distinctPriceCount: 0 };
  }

  const found: OrderablePath[] = [];

  walkMenuPaths(
    allMenuGroups,
    ({ group, path, names }) => {
      // Does the item hang directly in this group, visibly?
      const itemRef = group.items?.find((ref) => ref.itemId === itemId);
      if (!itemRef || itemRef.isHidden === true) return;
      const gid = group.menuGroupId as number;
      const menuGroupPath = [...path];
      found.push({
        menuGroupPath,
        menuGroupId: gid,
        pathNames: [...names],
        price: resolveItemPrice(item, menuGroupPath, gid, allMenuGroups),
      });
    },
    {
      ...(storefront ? { rootIds: storefront.rootIds } : {}),
      // Availability is per GROUP and per EDGE (a parent's override entry for
      // that child), exactly as before — only the source of children changed.
      enterGroup: (group) =>
        isMenuGroupAvailableForOnlineOrdering(group, now, timeZone) &&
        !(storefront?.hiddenSectionIds.has(group.menuGroupId as number) ?? false),
      enterEdge: ({ override }: MenuEdge) =>
        isSubgroupEdgeAvailableOnline(override ?? undefined, now, timeZone),
    },
  );

  const signatures = new Set(found.map((p) => priceSignature(p.price)));

  // Stable ordering: cheapest flat first, then shortest path. Keeps prompts and
  // golden tests deterministic.
  const paths = [...found].sort((a, b) => {
    const av = a.price.kind === 'flat' ? a.price.unitPrice : Number.POSITIVE_INFINITY;
    const bv = b.price.kind === 'flat' ? b.price.unitPrice : Number.POSITIVE_INFINITY;
    if (av !== bv) return av - bv;
    return a.menuGroupPath.length - b.menuGroupPath.length;
  });

  return {
    paths,
    isAmbiguous: signatures.size > 1,
    distinctPriceCount: signatures.size,
  };
}

/**
 * The single path to use, or `null` when the assistant must ask first.
 *
 * Returning `null` on ambiguity is deliberate and load-bearing: there is no
 * safe default. A silent tie-break here is a mis-charge, so the caller is
 * forced to surface the choice to the guest.
 */
export function resolveSinglePath(resolution: PathResolution): OrderablePath | null {
  if (resolution.paths.length === 0) return null;
  if (resolution.isAmbiguous) return null;
  return resolution.paths[0];
}
